import { useMemo, useState } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  IconButton,
  Tooltip,
  useToast,
  Collapse,
  Divider,
} from '@chakra-ui/react';
import {
  Copy,
  Check,
  Clock,
  Hash,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import type { ShortFormScript, ShortFormScriptsPayload, ShortFormValidationIssue } from '../lib/supabase';

interface ShortFormContentProps {
  payload: ShortFormScriptsPayload;
}

const DEFAULT_LABELS: Record<string, string> = {
  universal_60s: 'Universal 60s',
  direct_educational_60s: '60s Short-Form Clip 1',
  alternate_angle_60s: '60s Short-Form Clip 2',
  deeper_authority_90s: '90s Short-Form Clip',
};

const DEFAULT_STRATEGIES: Record<string, string> = {
  direct_educational_60s: 'Direct educational',
  alternate_angle_60s: 'Alternate angle',
  deeper_authority_90s: 'Deeper authority',
  universal_60s: 'Universal',
};

function deriveLabel(script: ShortFormScript, index: number): string {
  return script.clip_label ?? DEFAULT_LABELS[script.variant] ?? `Short-Form Clip ${index + 1}`;
}

function deriveStrategy(script: ShortFormScript): string | null {
  if (script.strategy) {
    return script.strategy
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
  return DEFAULT_STRATEGIES[script.variant] ?? null;
}

function normalizeScripts(payload: ShortFormScriptsPayload): ShortFormScript[] {
  if (!payload || !Array.isArray(payload.scripts)) {
    return [];
  }
  return payload.scripts.map((script, index) => ({
    ...script,
    clip_label: deriveLabel(script, index),
    strategy: deriveStrategy(script),
    target_duration_seconds: script.target_duration_seconds ?? (script.variant === 'deeper_authority_90s' ? 90 : 60),
  }));
}

function getValidationStatus(script: ShortFormScript): 'pass' | 'warn' | 'error' {
  if (script.validation.errors.length > 0) return 'error';
  if (
    script.validation.warnings.length > 0 ||
    script.validation.ban_list_status === 'warn' ||
    script.validation.duration_status === 'warn'
  ) {
    return 'warn';
  }
  return 'pass';
}

function ValidationStatusBadge({
  status,
  errorCount,
  warningCount,
}: {
  status: 'pass' | 'warn' | 'error';
  errorCount: number;
  warningCount: number;
}) {
  if (status === 'pass') {
    return (
      <HStack
        spacing={1}
        px={2}
        py={1}
        bg="green.50"
        color="green.700"
        borderRadius="md"
        fontSize="xs"
        fontWeight="bold"
        title="Both validators passed"
      >
        <CheckCircle2 size={12} />
        <Text fontSize="xs">Validated</Text>
      </HStack>
    );
  }
  if (status === 'warn') {
    return (
      <HStack
        spacing={1}
        px={2}
        py={1}
        bg="yellow.50"
        color="yellow.800"
        borderRadius="md"
        fontSize="xs"
        fontWeight="bold"
        title={`${warningCount} warning${warningCount === 1 ? '' : 's'}`}
      >
        <AlertTriangle size={12} />
        <Text fontSize="xs">{warningCount} warning{warningCount === 1 ? '' : 's'}</Text>
      </HStack>
    );
  }
  return (
    <HStack
      spacing={1}
      px={2}
      py={1}
      bg="red.50"
      color="red.700"
      borderRadius="md"
      fontSize="xs"
      fontWeight="bold"
      title={`${errorCount} error${errorCount === 1 ? '' : 's'}`}
    >
      <XCircle size={12} />
      <Text fontSize="xs">{errorCount} error{errorCount === 1 ? '' : 's'}</Text>
    </HStack>
  );
}

/**
 * Renders a list of validator issues as a coloured banner.
 *
 * Errors are always expanded — the user must see them immediately.
 * Warnings render in a collapsed/click-to-expand row by default so an
 * advisory length tip on a 200-word draft doesn't dominate the panel
 * above the actual script content.
 */
function IssueBanner({
  severity,
  issues,
}: {
  severity: 'error' | 'warning';
  issues: ShortFormValidationIssue[];
}) {
  const isError = severity === 'error';
  // Warnings start collapsed; errors stay expanded (loud signal for things
  // the editor must fix before publishing).
  const [isOpen, setIsOpen] = useState(isError);
  if (issues.length === 0) return null;
  const headerIcon = isError ? (
    <XCircle size={14} color="#c53030" />
  ) : (
    <AlertTriangle size={14} color="#b7791f" />
  );
  const headerLabel = isError
    ? `${issues.length} Error${issues.length === 1 ? '' : 's'}`
    : `${issues.length} Warning${issues.length === 1 ? '' : 's'}`;

  // For errors: header + expanded body in a single coloured panel.
  // For warnings: header is a clickable row; body lives inside Collapse.
  return (
    <Box
      p={3}
      bg={isError ? 'red.50' : 'yellow.50'}
      borderRadius="md"
      borderWidth={1}
      borderColor={isError ? 'red.200' : 'yellow.200'}
      mb={3}
    >
      <HStack
        spacing={2}
        mb={isError || isOpen ? 1 : 0}
        cursor={isError ? 'default' : 'pointer'}
        onClick={isError ? undefined : () => setIsOpen((v) => !v)}
        role={isError ? undefined : 'button'}
        aria-expanded={isError ? undefined : isOpen}
        userSelect="none"
      >
        {headerIcon}
        <Text
          fontSize="xs"
          fontWeight="bold"
          color={isError ? 'red.700' : 'yellow.800'}
          textTransform="uppercase"
          letterSpacing="wide"
          flex="1"
        >
          {headerLabel}
        </Text>
        {!isError && (
          <Box color="yellow.800" aria-hidden>
            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </Box>
        )}
      </HStack>
      <Collapse in={isError || isOpen} animateOpacity>
        <VStack align="stretch" spacing={1} pt={isError ? 0 : 1}>
          {issues.map((issue, idx) => (
            <Text
              key={idx}
              fontSize="xs"
              color={isError ? 'red.600' : 'yellow.800'}
              lineHeight="1.5"
            >
              • {issue.message}
              {issue.code && (
                <Text as="span" color="gray.500" ml={1}>
                  ({issue.code})
                </Text>
              )}
            </Text>
          ))}
        </VStack>
      </Collapse>
    </Box>
  );
}

function ClipSection({
  script,
  index,
  copiedKey,
  onCopy,
}: {
  script: ShortFormScript;
  index: number;
  copiedKey: string | null;
  onCopy: (text: string, key: string, successTitle: string) => Promise<void>;
}) {
  const fullText = `${script.hook.trim()}\n\n${script.body_markdown.trim()}\n\n${script.cta.trim()}`;
  const status = getValidationStatus(script);

  return (
    <Box
      p={4}
      bg="white"
      borderRadius="lg"
      borderWidth={1}
      borderColor="gray.200"
      boxShadow="sm"
    >
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between" align="flex-start" spacing={3} flexWrap="wrap">
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" fontWeight="bold" color="gray.900">
              {script.clip_label}
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              <Badge
                bg="purple.100"
                color="purple.800"
                fontSize="xs"
                px={2}
                py={1}
                borderRadius="md"
                textTransform="none"
              >
                {script.strategy}
              </Badge>
              <HStack spacing={1} px={2} py={1} bg="gray.100" borderRadius="md">
                <Clock size={12} />
                <Text fontSize="xs" fontWeight="medium">
                  ~{script.duration_estimate_seconds}s
                </Text>
              </HStack>
              <HStack spacing={1} px={2} py={1} bg="gray.100" borderRadius="md">
                <Hash size={12} />
                <Text fontSize="xs" fontWeight="medium">
                  {script.word_count} words
                </Text>
              </HStack>
              <ValidationStatusBadge
                status={status}
                errorCount={script.validation.errors.length}
                warningCount={script.validation.warnings.length}
              />
            </HStack>
          </VStack>

          <Button
            size="sm"
            variant="outline"
            colorScheme="brand"
            leftIcon={copiedKey === `full-${index}` ? <Check size={14} /> : <Copy size={14} />}
            onClick={() => onCopy(fullText, `full-${index}`, `${script.clip_label} copied`)}
          >
            {copiedKey === `full-${index}` ? 'Copied!' : 'Copy full script'}
          </Button>
        </HStack>

        <IssueBanner severity="error" issues={script.validation.errors} />
        <IssueBanner severity="warning" issues={script.validation.warnings} />

        <Box
          p={3}
          bg="brand.50"
          borderRadius="md"
          borderLeftWidth={4}
          borderLeftColor="brand.500"
          position="relative"
        >
          <HStack justify="space-between" mb={1}>
            <Text
              fontSize="2xs"
              fontWeight="bold"
              color="brand.700"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Hook
            </Text>
            <Tooltip label="Copy hook only" fontSize="xs">
              <IconButton
                aria-label={`Copy hook for ${script.clip_label}`}
                icon={copiedKey === `hook-${index}` ? <Check size={12} /> : <Copy size={12} />}
                size="xs"
                variant="ghost"
                colorScheme={copiedKey === `hook-${index}` ? 'green' : 'gray'}
                onClick={() => onCopy(script.hook, `hook-${index}`, `${script.clip_label} hook copied`)}
              />
            </Tooltip>
          </HStack>
          <Text fontSize="md" color="gray.900" fontWeight="semibold" lineHeight="1.5">
            {script.hook}
          </Text>
        </Box>

        <Box>
          <Text
            fontSize="2xs"
            fontWeight="bold"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wide"
            mb={1}
          >
            Body
          </Text>
          <Box position="relative">
            <Tooltip label="Copy body only" fontSize="xs">
              <IconButton
                aria-label={`Copy body for ${script.clip_label}`}
                icon={copiedKey === `body-${index}` ? <Check size={12} /> : <Copy size={12} />}
                size="xs"
                variant="ghost"
                position="absolute"
                top={2}
                right={2}
                zIndex={1}
                colorScheme={copiedKey === `body-${index}` ? 'green' : 'gray'}
                onClick={() =>
                  onCopy(
                    script.body_markdown,
                    `body-${index}`,
                    `${script.clip_label} body copied`
                  )
                }
              />
            </Tooltip>
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              borderWidth={1}
              borderColor="gray.200"
              fontSize="sm"
              color="gray.700"
              lineHeight="1.7"
              whiteSpace="pre-wrap"
              fontFamily="system-ui, -apple-system, sans-serif"
              maxH="320px"
              overflowY="auto"
            >
              {script.body_markdown}
            </Box>
          </Box>
        </Box>

        <Box p={3} bg="green.50" borderRadius="md" borderLeftWidth={4} borderLeftColor="green.500">
          <Text
            fontSize="2xs"
            fontWeight="bold"
            color="green.700"
            textTransform="uppercase"
            letterSpacing="wide"
            mb={1}
          >
            Call to Action
          </Text>
          <Text fontSize="sm" color="gray.900" fontWeight="medium" lineHeight="1.5">
            {script.cta}
          </Text>
        </Box>
      </VStack>
    </Box>
  );
}

export default function ShortFormContent({ payload }: ShortFormContentProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const toast = useToast();

  const scripts = useMemo(() => {
    return normalizeScripts(payload);
  }, [payload]);

  if (scripts.length === 0) {
    return (
      <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
        <Text fontSize="sm" color="gray.500">
          No short-form script available for this draft.
        </Text>
      </Box>
    );
  }

  const copyToClipboard = async (text: string, key: string, successTitle: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 2000);
      toast({
        title: successTitle,
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Browser clipboard access denied.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      {scripts.map((script, index) => (
        <Box key={script.id}>
          {index > 0 && <Divider mb={4} />}
          <ClipSection
            script={script}
            index={index}
            copiedKey={copiedKey}
            onCopy={copyToClipboard}
          />
        </Box>
      ))}
    </VStack>
  );
}
